import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface SocialAuthButtonsProps {
  mode: "login" | "signup";
}

/**
 * Enabled OAuth providers. Apple is temporarily disabled because the
 * Apple Developer account ($99/yr) isn't active yet. To re-enable:
 *   1. Complete Apple Developer setup (see docs)
 *   2. Enable Apple provider in Supabase dashboard
 *   3. Add "apple" back to this array, the button renders automatically
 */
const ENABLED_PROVIDERS = ["google"] as const;
type Provider = typeof ENABLED_PROVIDERS[number];

export function SocialAuthButtons({ mode }: SocialAuthButtonsProps) {
  const [loadingProvider, setLoadingProvider] = useState<string | null>(null);

  const handleOAuth = async (provider: Provider) => {
    setLoadingProvider(provider);

    // signInWithOAuth (not signUp) so Supabase links Google to an existing
    // email/password account when automatic identity linking is enabled.
    //
    // Sign-in must land on /dashboard — returning users were hitting /welcome
    // (signup redirect or /login → brief flash) which felt like a broken loop.
    // First-time OAuth signups also go to /dashboard; ProtectedRoute sends
    // users without a company to /onboarding. /welcome stays for email-confirm
    // links only (emailRedirectTo in useAuth.signUp).
    const redirectTo =
      mode === "signup"
        ? `${window.location.origin}/welcome`
        : `${window.location.origin}/dashboard`;

    const { error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo,
        // MS522: force Google's account-chooser on every sign-in instead of
        // letting Google decide between silent-reauth / picker / cold-login
        // (the cold-login screen was the bug — users with active Google
        // sessions were being asked for email+password instead of seeing a
        // one-click tile for their already-signed-in account).
        // `select_account` is the same prompt Notion, Linear, Figma use.
        queryParams: {
          prompt: "select_account",
        },
      },
    });

    if (error) {
      toast.error(error.message || `Failed to sign in with ${provider}`);
      setLoadingProvider(null);
    }
  };

  return (
    <div className="space-y-3">
      <div className="relative flex items-center gap-4 py-2">
        <div className="flex-1 border-t border-charcoal/10" />
        <span className="text-xs text-charcoal/40 uppercase tracking-wider">or continue with</span>
        <div className="flex-1 border-t border-charcoal/10" />
      </div>

      {ENABLED_PROVIDERS.includes("google") && (
        <button
          type="button"
          onClick={() => handleOAuth("google")}
          disabled={!!loadingProvider}
          className="flex w-full items-center justify-center gap-3 rounded-full border border-charcoal/15 bg-white px-6 py-3 text-sm font-medium text-navy transition-all hover:bg-charcoal/5 hover:shadow-sm disabled:opacity-50"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
            <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
            <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
            <path d="M3.964 10.706A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.038l3.007-2.332z" fill="#FBBC05"/>
            <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
          </svg>
          {loadingProvider === "google"
            ? "Connecting..."
            : mode === "signup"
              ? "Sign up with Google"
              : "Sign in with Google"}
        </button>
      )}

      {/*
        Apple button, re-enable by adding "apple" to ENABLED_PROVIDERS above.
        Keeps SVG + handler ready for instant re-activation.

        <button
          type="button"
          onClick={() => handleOAuth("apple" as Provider)}
          disabled={!!loadingProvider}
          className="flex w-full items-center justify-center gap-3 rounded-full border border-charcoal/15 bg-white px-6 py-3 text-sm font-medium text-navy transition-all hover:bg-charcoal/5 hover:shadow-sm disabled:opacity-50"
        >
          <svg width="18" height="18" viewBox="0 0 18 22" xmlns="http://www.w3.org/2000/svg">
            <path d="M14.94 11.58c-.02-2.2 1.8-3.26 1.88-3.31-1.02-1.5-2.62-1.7-3.19-1.73-1.36-.14-2.65.8-3.34.8-.69 0-1.76-.78-2.89-.76-1.49.02-2.86.87-3.63 2.2-1.54 2.68-.4 6.65 1.11 8.82.73 1.06 1.61 2.25 2.76 2.21 1.11-.04 1.53-.72 2.87-.72 1.34 0 1.72.72 2.89.7 1.19-.02 1.95-1.08 2.68-2.14.84-1.23 1.19-2.42 1.21-2.48-.03-.01-2.33-.89-2.35-3.54zM12.73 4.52c.61-.74 1.02-1.76.91-2.78-.88.04-1.94.58-2.57 1.32-.56.65-1.06 1.7-.92 2.7.98.08 1.97-.5 2.58-1.24z" fill="#1D3461"/>
          </svg>
          {loadingProvider === "apple" ? "Connecting..." : "Sign in with Apple"}
        </button>
      */}
    </div>
  );
}
